import {
    ApiResponse,
    AppErrorCode,
    NextcloudRequestConfig,
    ResponseError,
    webdavApi,
} from '@sendent/nextcloud-api';
import { WebdavApiErrorSubCode } from '@sendent/nextcloud-api/dist/models/error-model';
import { RetryOptions } from '@sendent/nextcloud-api/dist/models/webdav/retry-upload-model';
import { PARALLEL_UPLOAD_LIMIT } from '../helpers/constants';
import { getRepeatedFileName } from '../helpers/file';
import { RefBox } from '../helpers/refBox';
import { mapInChunks } from '../helpers/utils';
import { FileItem } from '../model';

// Holds the controller of the running chunked upload so the progress dialog can cancel it
export const chunkedUploadAbortControllerRefBox = new RefBox<AbortController>(
    new AbortController(),
);

const MAX_UPLOAD_RETRIES = 3;
const BASE_DELAY_MS = 1000;

const chunkedUploadRetryOptions: RetryOptions = {
    maxRetries: MAX_UPLOAD_RETRIES,
    baseDelayMs: BASE_DELAY_MS,
    calculateDelay: (retryAttempt, baseDelayMs) =>
        baseDelayMs * 2 ** (retryAttempt - 1),
};

export const registerChunkedUploadAbortController = (): AbortController => {
    const abortController = new AbortController();
    chunkedUploadAbortControllerRefBox.setValue(abortController);
    return abortController;
};

type UploadChunkedArgs = {
    requestConfig: NextcloudRequestConfig & { userId: string };
    destination: string;
    file: Blob;
    chunkSizeBytes: number;
    onUploadProgress: (totalUploadedBytes: number) => void;
    // Defaults to a freshly registered controller, pass one to share it between uploads
    signal?: AbortSignal;
};

export const uploadChunked = ({
    requestConfig,
    destination,
    file,
    chunkSizeBytes,
    onUploadProgress,
    signal,
}: UploadChunkedArgs): Promise<
    ApiResponse<null> | ResponseError<WebdavApiErrorSubCode>
> =>
    webdavApi.uploadChunkedFile({
        requestConfig,
        destination,
        content: file.stream(),
        fileSize: file.size,
        chunkSizeBytes,
        onUploadProgress,
        signal: signal ?? registerChunkedUploadAbortController().signal,
        retryOptions: chunkedUploadRetryOptions,
    });

export const countFilenameRepeats = async ({
    filename,
    folder,
    initialRepeatCount,
    requestConfig: { stubUrl, user, userId },
}: {
    filename: string;
    folder: string;
    initialRepeatCount: number;
    requestConfig: NextcloudRequestConfig & { userId: string };
}): Promise<number> => {
    let newName =
        initialRepeatCount > 1
            ? getRepeatedFileName(filename, initialRepeatCount + 1)
            : filename;
    let fileRepeatCount = initialRepeatCount;
    let fileExists = true;

    do {
        const result = await webdavApi.exists(
            stubUrl,
            user,
            userId,
            folder + '/' + newName,
        );

        fileExists = result.success && result.result;
        if (!fileExists) {
            continue;
        }

        newName = getRepeatedFileName(filename, ++fileRepeatCount);
    } while (fileExists);

    return fileRepeatCount;
};

type UploadFilesArgs = {
    files: FileItem[];
    folder: string;
    requestConfig: NextcloudRequestConfig & { userId: string };
    chunkSizeBytes: number;
    // Called with the total bytes uploaded so far across all files
    onUploadProgress?: (totalUploadedBytes: number) => void;
};

type UploadFileResult = {
    response: ApiResponse | ResponseError;
    filename: string;
};

// Same shape the chunked upload returns when its signal aborts mid-transfer
const cancelledUploadError: ResponseError<WebdavApiErrorSubCode> = {
    success: false,
    result: {
        message: 'Upload cancelled',
        errorCode: AppErrorCode.webdavApi,
        errorSubCode: WebdavApiErrorSubCode.NetworkError,
    },
};

// Uploads at most PARALLEL_UPLOAD_LIMIT files at a time, results keep the order of `files`
export const uploadFilesInParallel = async ({
    files,
    folder,
    chunkSizeBytes,
    onUploadProgress = () => {},
    requestConfig,
}: UploadFilesArgs): Promise<{
    uploadResponses: (ApiResponse | ResponseError)[];
    finalFilenames: string[];
}> => {
    // One controller for the whole batch so cancelling aborts every streamed upload
    const hasStreamedFiles = files.some((file) => file.readableContent);
    const signal = hasStreamedFiles
        ? registerChunkedUploadAbortController().signal
        : undefined;

    // Each upload reports its own cumulative byte count, the progress bar needs the sum
    const uploadedBytesPerFile = files.map(() => 0);
    const reportProgress = (index: number) => (uploadedBytes: number) => {
        uploadedBytesPerFile[index] = uploadedBytes;
        onUploadProgress(
            uploadedBytesPerFile.reduce((sum, bytes) => sum + bytes, 0),
        );
    };

    const results = await mapInChunks(
        files,
        PARALLEL_UPLOAD_LIMIT,
        async (file, index): Promise<UploadFileResult> => {
            // Cancelling aborts the running uploads, files not started yet are skipped
            if (signal?.aborted) {
                return { response: cancelledUploadError, filename: file.name };
            }

            const fileExistsRes = await webdavApi.exists(
                requestConfig.stubUrl,
                requestConfig.user,
                requestConfig.userId,
                `${folder}/${file.name}`,
            );

            if (!fileExistsRes.success) {
                return { response: fileExistsRes, filename: file.name };
            }

            let filename = file.name;
            if (fileExistsRes.result) {
                const filenameRepeatCount = await countFilenameRepeats({
                    filename: file.name,
                    folder,
                    initialRepeatCount: 1,
                    requestConfig,
                });
                filename = getRepeatedFileName(file.name, filenameRepeatCount);
            }

            const destination = `${folder}/${filename}`;
            const response = file.readableContent
                ? await uploadChunked({
                      requestConfig,
                      destination,
                      file: file.readableContent,
                      chunkSizeBytes,
                      onUploadProgress: reportProgress(index),
                      signal,
                  })
                : await webdavApi.copyOrCreateFile(
                      requestConfig.stubUrl,
                      requestConfig.user,
                      requestConfig.userId,
                      filename,
                      destination,
                      file.content,
                      reportProgress(index),
                  );

            return { response, filename };
        },
    );

    return {
        uploadResponses: results.map(({ response }) => response),
        finalFilenames: results.map(({ filename }) => filename),
    };
};
